import type { TooltipOptions, LegendOptions, SeriesOptionsType, GradientColorObject, PatternObject } from 'highcharts'
import type { ISeries } from '~/types/series'

import { getNumberWithSpaces } from './number'

import { analyticsColors } from '~/const/chart'
import dayjs from '~/utils/dayjs'

export const isEmptyData = (series?: { data?: unknown[] }): boolean => {
  return !(series && series.data && series.data.length > 0 && series.data.find((s: any) => s.data && s.data.length))
}

export const formatTooltipValue = (value: number): string => {
  if (value % 1 === 0) {
    // NOTE: positive integers (e.g. 1234)
    return getNumberWithSpaces(value)
  } else if (value < 1) {
    const [integerStr, decimalStr] = parseFloat(value.toFixed(4)).toString().split('.')
    return `${getNumberWithSpaces(integerStr)}${decimalStr ? `.${decimalStr}` : ''}`
  }

  return getNumberWithSpaces(parseFloat(value.toFixed(2)))
}

export const getSeriesWithTrend = (series: ISeries[], period = 7): ISeries[] => {
  const seriesWithTrend: ISeries[] = []

  const normalSeriesOptions = (index: number): Partial<SeriesOptionsType> => ({
    color: `${analyticsColors[index]}` + Math.round(0.6 * 255).toString(16),
    id: `${index}_for_trend`,
    opacity: 0.3,
  })

  const seriesWithTrendOptions = (index: number): Partial<SeriesOptionsType> => ({
    allowPointSelect: true,
    color: analyticsColors[index],
    linkedTo: `${index}_for_trend`,
    marker: {
      enabled: false,
    },
    opacity: 1,
    params: {
      period,
    },
    showInLegend: true,
    type: 'ema',
    visible: true,
  })

  series.forEach((serie: ISeries, index: number) => {
    seriesWithTrend.push({
      ...normalSeriesOptions(index),
      ...serie,
    })
    seriesWithTrend.push({
      ...seriesWithTrendOptions(index),
      ...serie,
      name: `${serie.name} (Trend)`,
    })
  })

  return seriesWithTrend
}

export const getLegendOptions = (chartType: 'column' | 'line'): LegendOptions => {
  switch (chartType) {
    case 'column':
      return {
        squareSymbol: true,
        symbolHeight: 12,
        symbolRadius: 0,
        symbolWidth: 15,
      }
    default:
      return {
        itemStyle: {
          lineHeight: '24px',
        },
        symbolHeight: 20,
        symbolWidth: 20,
      }
  }
}

interface StackingTooltipPoint {
  color: string
  date: string
  name: string
  value: number
}

export const tooltipUtils = {
  getHeader: ({ value }: { value: string }): string => {
    return `<div style="font-weight:600">${value}</div></br>`
  },
  getLabel: ({ color, value }: { color?: string | GradientColorObject | PatternObject; value: string }): string => {
    return `<span style="color: ${color}">• </span><span style="color: ${color}">${value}</span>`
  },
  getStackingToolTip: (): TooltipOptions => {
    return {
      formatter: function (this) {
        // NOTE: this.points exist when shared property is true
        // @ts-expect-error this.points does not exist on type Point
        const _points = this.points
        if (!_points) return ''

        const totalPerStack: number[] = []
        const pointsPerStack: StackingTooltipPoint[][] = []

        for (let i = 0; i < _points.length; i++) {
          const point = _points[i]
          const { color, series, total, x, y } = point
          const { userOptions } = series

          if (!userOptions || !total || userOptions.stack === undefined) continue

          const { name, stack } = userOptions

          const stackIndex = typeof stack === 'string' ? parseInt(stack) : stack

          totalPerStack[stackIndex] = total

          const newPoint = {
            color,
            date: dayjs(x).format('MMM D, YYYY'),
            name,
            value: y,
          }

          if (!pointsPerStack[stackIndex]) {
            pointsPerStack[stackIndex] = [newPoint]
          } else {
            pointsPerStack[stackIndex].push(newPoint)
          }
        }

        if (pointsPerStack.length > 0) {
          const header = tooltipUtils.getHeader({ value: pointsPerStack.find((stack) => stack)?.[0]?.date || '' })

          let body = ''

          for (let x = 0; x < pointsPerStack.length; x++) {
            let total = ''
            const stack = pointsPerStack[x]

            for (let y = 0; stack && y < stack.length; y++) {
              const { color, name, value } = stack[y]

              const label = tooltipUtils.getLabel({ color, value: name || '' })

              const formattedValue = value
                ? tooltipUtils.getValue({
                    value: formatTooltipValue(value),
                  })
                : 'no value'

              body = `${body} <div>${label}: ${formattedValue}<div>`
            }

            if (stack && stack.length > 1) {
              total = `<div style="font-size:11px;font-weight:600;margin-top:10px">Total: ${formatTooltipValue(
                totalPerStack[x]
              )}</div>`
            }

            body = `${body} ${total}<hr>`
          }

          return header + body
        }
        return '<div>No complete data available for this App</div></br>'
      },
    }
  },
  getValue: ({ suffix, value }: { suffix?: string; value: string | number }): string => {
    return `<b>${value}${suffix || ''}</b>`
  },
}

/**
 * It takes a string, removes the substrings "_inf_" or "_sup_", and returns it before "_trend"
 * @param seriesName A string, typically the name of a trend, infimum or supremum series
 * @return That string formatted so it is as close as possible to the name of the regular data series the series whose name was passed as argument relates to
 */
export const removeInfSupTrendFromSeriesName = (seriesName: string): string => {
  let name = seriesName

  if (name.includes('_inf_') || name.includes('_sup_')) {
    const nameSubstrings = name.includes('_inf_') ? name.split('_inf_') : name.split('_sup_')
    name = nameSubstrings[0] + '_' + nameSubstrings[1]
  }

  if (name.includes('_trend')) {
    const nameSubstring = name.split('_trend')
    name = nameSubstring[0]
  }

  return name
}
